import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { Layout } from '../../components/layout/Layout';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminNav } from '../../components/admin/AdminNav';
import { AdminLoginCard } from '../../components/admin/AdminLoginCard';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useProducts } from '../../hooks/useProducts';
import { getStockMap, STOCK_UPDATED_EVENT } from '../../utils/adminStorage';
import { db } from '../../firebase';

const toNonNegativeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
};

const getProcurementLineTotalPrice = (item) => {
  const directTotal =
    toNonNegativeNumber(item?.totalPrice)
    ?? toNonNegativeNumber(item?.linePrice)
    ?? toNonNegativeNumber(item?.price);
  if (directTotal !== null) return directTotal;

  const qty = Math.max(1, Number(item?.qty) || 1);
  const unitPrice = toNonNegativeNumber(item?.unitPrice);
  if (unitPrice === null) return null;
  return unitPrice * qty;
};

const formatFt = (value) => `${Number(value || 0).toLocaleString('hu-HU')} Ft`;
const FINANCE_TABS = [
  { id: 'prices', label: 'Termékárak' },
  { id: 'expenses', label: 'Költségek' },
];

export default function AdminFinance() {
  const { user, loading } = useAdminAuth();
  const { products, loading: loadingProducts, error: productsError } = useProducts();
  const [expenses, setExpenses] = useState([]);
  const [procurements, setProcurements] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryAdjustments, setInventoryAdjustments] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [loadingProcurements, setLoadingProcurements] = useState(true);
  const [loadingAdjustments, setLoadingAdjustments] = useState(true);
  const [error, setError] = useState('');
  const [pricingError, setPricingError] = useState('');
  const [priceDraftByProductId, setPriceDraftByProductId] = useState({});
  const [plannedPriceDraftByProductId, setPlannedPriceDraftByProductId] = useState({});
  const [savingProductId, setSavingProductId] = useState('');
  const [activeFinanceView, setActiveFinanceView] = useState('prices');
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [procurementFilter, setProcurementFilter] = useState('all');
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState('all');
  const [showActiveProductsOnly, setShowActiveProductsOnly] = useState(false);
  const [stockMap, setStockMap] = useState(() => getStockMap());
  const [form, setForm] = useState({
    name: '',
    amount: '',
    startMonth: '',
    endMonth: '',
  });

  useEffect(() => {
    if (!user) {
      setExpenses([]);
      setLoadingExpenses(false);
      return undefined;
    }
    setLoadingExpenses(true);
    const q = query(collection(db, 'expenses'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setExpenses(next);
        setLoadingExpenses(false);
        setError('');
      },
      () => {
        setExpenses([]);
        setLoadingExpenses(false);
        setError('Nem sikerült betölteni az általános költségeket.');
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setProcurements([]);
      setLoadingProcurements(false);
      return undefined;
    }

    setLoadingProcurements(true);
    const q = query(collection(db, 'purchase'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setProcurements(next);
        setLoadingProcurements(false);
        setPricingError('');
      },
      () => {
        setProcurements([]);
        setLoadingProcurements(false);
        setPricingError('Nem sikerült betölteni a beszerzési adatokat.');
      },
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setSuppliers([]);
      return undefined;
    }
    const q = query(collection(db, 'suppliers'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setSuppliers(next);
      },
      () => {
        setSuppliers([]);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setInventoryAdjustments([]);
      setLoadingAdjustments(false);
      return undefined;
    }

    setLoadingAdjustments(true);
    const q = query(collection(db, 'inventoryAdjustments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setInventoryAdjustments(next);
        setLoadingAdjustments(false);
      },
      () => {
        setInventoryAdjustments([]);
        setLoadingAdjustments(false);
        setPricingError('Nem sikerült betölteni a készlet korrekciós adatokat.');
      },
    );

    return () => unsub();
  }, [user]);

  useEffect(() => {
    setPriceDraftByProductId((prev) => {
      const next = {};
      products.forEach((product) => {
        const fallback = String(Number(product?.price) || 0);
        next[product.id] = prev[product.id] ?? fallback;
      });
      return next;
    });
  }, [products]);

  useEffect(() => {
    setPlannedPriceDraftByProductId((prev) => {
      const next = {};
      products.forEach((product) => {
        const plannedValue = toNonNegativeNumber(product?.plannedPurchasePrice);
        const fallback = plannedValue === null ? '' : String(plannedValue);
        next[product.id] = prev[product.id] ?? fallback;
      });
      return next;
    });
  }, [products]);

  useEffect(() => {
    setStockMap(getStockMap());
  }, [user]);

  useEffect(() => {
    if (activeFinanceView !== 'expenses') {
      setShowExpenseForm(false);
    }
  }, [activeFinanceView]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncStock = (event) => {
      if (
        !event
        || event.type === STOCK_UPDATED_EVENT
        || event.key === null
        || event.key === 'lumenverde-stock'
      ) {
        setStockMap(getStockMap());
      }
    };
    window.addEventListener(STOCK_UPDATED_EVENT, syncStock);
    window.addEventListener('storage', syncStock);
    window.addEventListener('focus', syncStock);
    return () => {
      window.removeEventListener(STOCK_UPDATED_EVENT, syncStock);
      window.removeEventListener('storage', syncStock);
      window.removeEventListener('focus', syncStock);
    };
  }, []);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const monthDiffInclusive = (startValue, endValue) => {
    if (!startValue || !endValue) return 0;
    const [startYear, startMonth] = startValue.split('-').map(Number);
    const [endYear, endMonth] = endValue.split('-').map(Number);
    if (!startYear || !startMonth || !endYear || !endMonth) return 0;
    return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
  };

  const handleAddExpense = async (event) => {
    event.preventDefault();
    setError('');
    const name = form.name.trim();
    const amount = Number(form.amount);
    const { startMonth, endMonth } = form;

    if (!name || !startMonth || !endMonth || !Number.isFinite(amount) || amount <= 0) {
      setError('Minden mező kitöltése kötelező, az összeg legyen pozitív.');
      return;
    }

    const months = monthDiffInclusive(startMonth, endMonth);
    if (months <= 0) {
      setError('A kezdő hónap nem lehet később, mint a záró hónap.');
      return;
    }

    try {
      await addDoc(collection(db, 'expenses'), {
        name,
        amount,
        startMonth,
        endMonth,
        months,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setForm({ name: '', amount: '', startMonth: '', endMonth: '' });
    } catch {
      setError('Nem sikerült elmenteni a költséget.');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Biztosan törlöd ezt a tételt?')) return;
    try {
      await deleteDoc(doc(db, 'expenses', id));
    } catch {
      setError('Nem sikerült törölni a tételt.');
    }
  };

  const computedExpenses = useMemo(
    () =>
      expenses.map((item) => {
        const months = item.months || monthDiffInclusive(item.startMonth, item.endMonth) || 1;
        const amount = Number(item.amount) || 0;
        return {
          ...item,
          months,
          monthly: months > 0 ? amount / months : amount,
        };
      }),
    [expenses],
  );

  const sortedProducts = useMemo(
    () =>
      [...products].sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), 'hu'),
      ),
    [products],
  );

  const productNameById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      if (product?.id) {
        map.set(product.id, product.name || product.id);
      }
    });
    return map;
  }, [products]);

  const activeSuppliers = useMemo(
    () => suppliers.filter((supplier) => supplier?.isActive !== false),
    [suppliers],
  );

  const supplierOptions = useMemo(() => {
    const names = new Set();
    activeSuppliers.forEach((supplier) => {
      const name = String(supplier?.name || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names.values()).sort((a, b) => a.localeCompare(b, 'hu'));
  }, [activeSuppliers]);

  useEffect(() => {
    if (selectedSupplierFilter === 'all') return;
    if (!supplierOptions.includes(selectedSupplierFilter)) {
      setSelectedSupplierFilter('all');
    }
  }, [selectedSupplierFilter, supplierOptions]);

  const supplierByProductId = useMemo(() => {
    const map = new Map();
    const normalizeName = (value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '');
    const productIdByName = new Map();
    products.forEach((product) => {
      const key = normalizeName(product?.name);
      if (key && !productIdByName.has(key)) {
        productIdByName.set(key, product.id);
      }
    });

    procurements.forEach((procurement) => {
      const supplierName = String(procurement?.supplier || '').trim();
      if (!supplierName) return;
      const items = Array.isArray(procurement?.items) ? procurement.items : [];
      items.forEach((item) => {
        const productId = item?.productId;
        if (!productId) return;
        if (!map.has(productId)) {
          map.set(productId, new Set());
        }
        map.get(productId).add(supplierName);
      });
    });

    activeSuppliers.forEach((supplier) => {
      const supplierName = String(supplier?.name || '').trim();
      if (!supplierName) return;
      const items = Array.isArray(supplier?.products) ? supplier.products : [];
      items.forEach((item) => {
        const directId = String(item?.productId || '').trim();
        const nameKey = normalizeName(item?.productName);
        const resolvedId = directId || productIdByName.get(nameKey);
        if (!resolvedId) return;
        if (!map.has(resolvedId)) {
          map.set(resolvedId, new Set());
        }
        map.get(resolvedId).add(supplierName);
      });
    });

    return map;
  }, [procurements, activeSuppliers, products]);

  const filteredProducts = useMemo(
    () =>
      sortedProducts.filter((product) => {
        if (showActiveProductsOnly && product?.isActive === false) return false;
        if (selectedSupplierFilter === 'all') return true;
        return supplierByProductId.get(product.id)?.has(selectedSupplierFilter);
      }),
    [selectedSupplierFilter, showActiveProductsOnly, sortedProducts, supplierByProductId],
  );

  const receivedTotalsByProductId = useMemo(() => {
    const map = new Map();

    procurements.forEach((procurement) => {
      if (!procurement?.receivedAt) return;
      const items = Array.isArray(procurement?.items) ? procurement.items : [];
      items.forEach((item) => {
        const productId = item?.productId;
        if (!productId) return;

        const qty = Math.max(1, Number(item?.qty) || 1);
        const totalPrice = getProcurementLineTotalPrice(item);
        if (totalPrice === null) return;

        const current = map.get(productId) || {
          totalPrice: 0,
          qty: 0,
        };
        current.totalPrice += totalPrice;
        current.qty += qty;
        map.set(productId, current);
      });
    });

    return map;
  }, [procurements]);

  const scrapedTotalsByProductId = useMemo(() => {
    const map = new Map();

    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'scrap') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;

      const current = map.get(productId) || 0;
      map.set(productId, current + amount);
    });

    return map;
  }, [inventoryAdjustments]);

  const packedTotalsByProductId = useMemo(() => {
    const map = new Map();
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'order_pack') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;
      map.set(productId, (map.get(productId) || 0) + amount);
    });
    return map;
  }, [inventoryAdjustments]);

  const unpackedTotalsByProductId = useMemo(() => {
    const map = new Map();
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'order_unpack') return;
      const productId = entry?.productId;
      if (!productId) return;
      const amount = toNonNegativeNumber(entry?.amount);
      if (amount === null) return;
      map.set(productId, (map.get(productId) || 0) + amount);
    });
    return map;
  }, [inventoryAdjustments]);

  const procurementTotals = useMemo(() => {
    let totalValue = 0;
    let inTransitValue = 0;
    let receivedValue = 0;
    let missingLineCount = 0;

    procurements.forEach((procurement) => {
      const isReceived = Boolean(procurement?.receivedAt);
      if (procurementFilter === 'received' && !isReceived) return;
      if (procurementFilter === 'in_transit' && isReceived) return;

      const items = Array.isArray(procurement?.items) ? procurement.items : [];
      let procurementTotal = 0;
      items.forEach((item) => {
        const lineTotal = getProcurementLineTotalPrice(item);
        if (lineTotal === null) {
          missingLineCount += 1;
          return;
        }
        procurementTotal += lineTotal;
      });

      if (procurementTotal <= 0) return;
      totalValue += procurementTotal;
      if (isReceived) {
        receivedValue += procurementTotal;
      } else {
        inTransitValue += procurementTotal;
      }
    });

    return {
      totalValue,
      inTransitValue,
      receivedValue,
      missingLineCount,
    };
  }, [procurements, procurementFilter]);

  const procurementLineItems = useMemo(() => {
    const rows = [];
    procurements.forEach((procurement) => {
      const isReceived = Boolean(procurement?.receivedAt);
      if (procurementFilter === 'received' && !isReceived) return;
      if (procurementFilter === 'in_transit' && isReceived) return;

      const supplier = procurement?.supplier || '—';
      const statusLabel = isReceived ? 'Készleten' : 'Úton';
      const items = Array.isArray(procurement?.items) ? procurement.items : [];
      items.forEach((item, index) => {
        const qty = Math.max(1, Number(item?.qty) || 1);
        const totalPrice = getProcurementLineTotalPrice(item);
        const directUnit = toNonNegativeNumber(item?.unitPrice);
        const unitPrice = directUnit !== null
          ? directUnit
          : (totalPrice !== null ? totalPrice / qty : null);
        const productId = item?.productId || '';
        const productName =
          item?.productName
          || productNameById.get(productId)
          || productId
          || '—';
        rows.push({
          key: `${procurement.id || 'proc'}-${index}`,
          supplier,
          statusLabel,
          productId,
          productName,
          qty,
          totalPrice,
          unitPrice,
        });
      });
    });
    return rows;
  }, [procurements, productNameById, procurementFilter]);

  const handleProductPriceDraftChange = (productId, value) => {
    setPriceDraftByProductId((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handlePlannedPriceDraftChange = (productId, value) => {
    setPlannedPriceDraftByProductId((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const handleSaveProductPrice = async (product) => {
    const draft = priceDraftByProductId[product.id] ?? String(Number(product.price) || 0);
    const nextPrice = Number(draft);
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
      setPricingError('Az eladási ár csak 0 vagy pozitív szám lehet.');
      return;
    }

    const plannedDraft = plannedPriceDraftByProductId[product.id] ?? '';
    const hasPlannedDraft = plannedDraft !== '' && plannedDraft !== null && plannedDraft !== undefined;
    const plannedParsed = hasPlannedDraft ? Number(plannedDraft) : null;
    if (hasPlannedDraft && (!Number.isFinite(plannedParsed) || plannedParsed < 0)) {
      setPricingError('A tervár csak 0 vagy pozitív szám lehet, vagy hagyd üresen.');
      return;
    }

    const normalizedNextPrice = Math.round(nextPrice);
    const normalizedPlannedPrice = hasPlannedDraft ? Math.round(plannedParsed) : null;
    const currentPrice = Number(product.price) || 0;
    const currentPlannedPrice = toNonNegativeNumber(product?.plannedPurchasePrice);
    const normalizedCurrentPlanned =
      currentPlannedPrice === null ? null : Math.round(currentPlannedPrice);
    if (
      normalizedNextPrice === currentPrice
      && normalizedPlannedPrice === normalizedCurrentPlanned
    ) {
      return;
    }

    setSavingProductId(product.id);
    try {
      await setDoc(
        doc(db, 'products', product.id),
        {
          price: normalizedNextPrice,
          plannedPurchasePrice: normalizedPlannedPrice,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      setPriceDraftByProductId((prev) => ({
        ...prev,
        [product.id]: String(normalizedNextPrice),
      }));
      setPlannedPriceDraftByProductId((prev) => ({
        ...prev,
        [product.id]: normalizedPlannedPrice === null ? '' : String(normalizedPlannedPrice),
      }));
      setPricingError('');
    } catch {
      setPricingError('Nem sikerült frissíteni az árakat.');
    } finally {
      setSavingProductId('');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="rounded-3xl border border-[#0F3D2C] p-8 md:p-10 bg-card/60">
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Admin / Pénzügy</h1>
            <AdminLoginCard description="A pénzügyi adatokhoz jelentkezz be Google fiókkal." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <AdminHeader title="Admin / Pénzügy" />
          <AdminNav />

          <div className="mt-8 flex flex-wrap gap-2 border-b border-border/70">
            {FINANCE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveFinanceView(tab.id)}
                className={`border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  activeFinanceView === tab.id
                    ? 'border-[#0F3D2C] text-[#0F3D2C]'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {activeFinanceView === 'expenses' ? (
              <>
            <div className="space-y-6">
              {showExpenseForm ? (
                <div className="rounded-3xl border border-border/70 bg-card p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="font-display text-xl font-semibold">Új költség</h2>
                    <button
                      type="button"
                      onClick={() => setShowExpenseForm(false)}
                      className="btn-secondary text-sm"
                    >
                      Bezárás
                    </button>
                  </div>
                  <form onSubmit={handleAddExpense} className="grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium mb-1">Költség neve</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleFormChange}
                        className="input-zinga"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Összeg</label>
                      <input
                        name="amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.amount}
                        onChange={handleFormChange}
                        className="input-zinga"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Időszak kezdete</label>
                      <input
                        name="startMonth"
                        type="month"
                        value={form.startMonth}
                        onChange={handleFormChange}
                        className="input-zinga"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Időszak vége</label>
                      <input
                        name="endMonth"
                        type="month"
                        value={form.endMonth}
                        onChange={handleFormChange}
                        className="input-zinga"
                      />
                    </div>
                    <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                      {error && <span className="text-xs text-destructive">{error}</span>}
                      <button type="submit" className="btn-primary text-sm">
                        + Költség hozzáadása
                      </button>
                    </div>
                  </form>
                </div>
              ) : null}

              <div className="rounded-3xl border border-border/70 bg-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h2 className="font-display text-xl font-semibold">Általános költségek</h2>
                  <button
                    type="button"
                    onClick={() => setShowExpenseForm(true)}
                    className="btn-secondary text-sm"
                  >
                    + Új költség
                  </button>
                </div>
                {error && (
                  <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive mb-4">
                    {error}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border/70">
                        <th className="py-3 pr-4">Költség</th>
                        <th className="py-3 pr-4">Összeg</th>
                        <th className="py-3 pr-4">Időszak</th>
                        <th className="py-3 pr-4">Havi</th>
                        <th className="py-3 pr-0 text-right">Művelet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingExpenses ? (
                        <tr>
                          <td className="py-4 text-sm text-muted-foreground" colSpan={5}>
                            Betöltés...
                          </td>
                        </tr>
                      ) : computedExpenses.length === 0 ? (
                        <tr>
                          <td className="py-4 text-sm text-muted-foreground" colSpan={5}>
                            Nincs rögzített költség.
                          </td>
                        </tr>
                      ) : (
                        computedExpenses.map((item) => (
                          <tr key={item.id} className="border-b border-border/40 last:border-0">
                            <td className="py-3 pr-4 font-medium">{item.name}</td>
                            <td className="py-3 pr-4">
                              {Number(item.amount || 0).toLocaleString('hu-HU')} Ft
                            </td>
                            <td className="py-3 pr-4">
                              {item.startMonth} – {item.endMonth}
                            </td>
                            <td className="py-3 pr-4">
                              {Number(item.monthly || 0).toLocaleString('hu-HU', {
                                maximumFractionDigits: 2,
                              })}{' '}
                              Ft
                            </td>
                            <td className="py-3 pr-0 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteExpense(item.id)}
                                className="btn-secondary text-xs text-destructive"
                              >
                                Törlés
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border/70 bg-card p-6">
              <div className="flex items-center justify-between gap-3 mb-4 flex-nowrap">
                <h2 className="font-display text-xl font-semibold">Beszerzés</h2>
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl border border-border/60 bg-secondary/30 px-3 py-2 text-[12px]">
                    <span className="text-muted-foreground">Költség</span>{' '}
                    <span className="font-semibold">{formatFt(procurementTotals.totalValue)}</span>
                  </div>
                  <select
                    value={procurementFilter}
                    onChange={(event) => setProcurementFilter(event.target.value)}
                    className="input-zinga w-36 shrink-0 text-[12px] py-2"
                  >
                    <option value="all">Mind</option>
                    <option value="in_transit">Úton lévő</option>
                    <option value="received">Készleten</option>
                  </select>
                </div>
              </div>

              {procurementTotals.missingLineCount > 0 ? (
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-700 mb-4">
                  {procurementTotals.missingLineCount} tételnél hiányzik az ár, ezek nem
                  szerepelnek az összesítésben.
                </div>
              ) : null}

              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/70">
                      <th className="py-3 pr-4">Szállító</th>
                      <th className="py-3 pr-4">Státusz</th>
                      <th className="py-3 pr-4">Termék</th>
                      <th className="py-3 pr-4">DB</th>
                      <th className="py-3 pr-4">Egységár</th>
                      <th className="py-3 pr-0 text-right">Összérték</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingProcurements ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={7}>
                          Betöltés...
                        </td>
                      </tr>
                    ) : procurementLineItems.length === 0 ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={7}>
                          Nincs beszerzési tétel.
                        </td>
                      </tr>
                    ) : (
                      procurementLineItems.map((row) => (
                        <tr key={row.key} className="border-b border-border/40 last:border-0">
                          <td className="py-3 pr-4 font-medium">{row.supplier}</td>
                          <td className="py-3 pr-4">{row.statusLabel}</td>
                          <td className="py-3 pr-4">
                            <p className="font-medium">{row.productName}</p>
                            {row.productId ? (
                              <p className="text-xs text-muted-foreground">ID: {row.productId}</p>
                            ) : null}
                          </td>
                          <td className="py-3 pr-4">{row.qty}</td>
                          <td className="py-3 pr-4">
                            {row.unitPrice !== null ? formatFt(row.unitPrice) : '—'}
                          </td>
                          <td className="py-3 pr-0 text-right">
                            {row.totalPrice !== null ? formatFt(row.totalPrice) : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            ) : null}

            {activeFinanceView === 'prices' ? (
            <div className="order-first lg:order-first lg:col-span-2 rounded-3xl border border-border/70 bg-card p-6">
              <div className="mb-4">
                <h2 className="font-display text-xl font-semibold">Termékárak</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  A beszerzési egységár a beérkeztetett beszerzések gyűjtött összértékéből és a
                  selejtezések + összecsomagolt rendelések levonásából számol: (összérték -
                  selejt - összecsomagolt + visszanyitott) / raktár darab (úton lévő nélkül). Az
                  eladási ár itt is szerkeszthető, és azonnal a Termékek menüben is frissül.
                </p>
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-3">
                <label className="text-xs font-medium text-muted-foreground">
                  Szállító szűrő
                </label>
                <select
                  value={selectedSupplierFilter}
                  onChange={(event) => setSelectedSupplierFilter(event.target.value)}
                  className="input-zinga max-w-[220px]"
                >
                  <option value="all">Minden szállító</option>
                  {supplierOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showActiveProductsOnly}
                    onChange={(event) => setShowActiveProductsOnly(event.target.checked)}
                  />
                  Csak aktív termékek
                </label>
              </div>

              {(pricingError || productsError) && (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive mb-4">
                  {pricingError || productsError}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border/70">
                      <th className="py-3 pr-4">Termék</th>
                      <th className="py-3 pr-4">Tervár (Ft)</th>
                      <th className="py-3 pr-4">Tervár árrés (%)</th>
                      <th className="py-3 pr-4">Beszerzési egységár</th>
                      <th className="py-3 pr-4">Árrés (%)</th>
                      <th className="py-3 pr-4">Eladási ár (Ft)</th>
                      <th className="py-3 pr-0 text-right">Művelet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingProducts || loadingProcurements || loadingAdjustments ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={7}>
                          Betöltés...
                        </td>
                      </tr>
                    ) : filteredProducts.length === 0 ? (
                      <tr>
                        <td className="py-4 text-sm text-muted-foreground" colSpan={7}>
                          {selectedSupplierFilter === 'all'
                            ? (
                              showActiveProductsOnly
                                ? 'Nincs aktív termék.'
                                : 'Nincs rögzített termék.'
                            )
                            : (
                              showActiveProductsOnly
                                ? 'Ehhez a szállítóhoz nincs aktív termék.'
                                : 'Ehhez a szállítóhoz nincs termék.'
                            )}
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((product) => {
                        const received = receivedTotalsByProductId.get(product.id) || null;
                        const scraped = Number(scrapedTotalsByProductId.get(product.id) || 0);
                        const packed = Number(packedTotalsByProductId.get(product.id) || 0);
                        const unpacked = Number(unpackedTotalsByProductId.get(product.id) || 0);
                        const netTotalValue = Math.max(
                          0,
                          (Number(received?.totalPrice) || 0) - scraped - packed + unpacked,
                        );
                        const stockQty = Math.max(0, Number(stockMap[product.id]) || 0);
                        const unitPurchasePrice =
                          netTotalValue > 0 && stockQty > 0
                            ? netTotalValue / stockQty
                            : null;
                        const currentPrice = Number(product.price) || 0;
                        const marginPercent =
                          unitPurchasePrice && unitPurchasePrice > 0
                            ? ((currentPrice / unitPurchasePrice) - 1) * 100
                            : null;
                        const draftValue =
                          priceDraftByProductId[product.id] ?? String(currentPrice);
                        const parsedDraft = Number(draftValue);
                        const isDraftValid =
                          draftValue !== '' && Number.isFinite(parsedDraft) && parsedDraft >= 0;
                        const normalizedDraft = isDraftValid ? Math.round(parsedDraft) : null;
                        const currentPlannedPrice = toNonNegativeNumber(product?.plannedPurchasePrice);
                        const normalizedCurrentPlanned =
                          currentPlannedPrice === null ? null : Math.round(currentPlannedPrice);
                        const plannedDraftValue =
                          plannedPriceDraftByProductId[product.id]
                          ?? (currentPlannedPrice === null ? '' : String(currentPlannedPrice));
                        const plannedParsed = Number(plannedDraftValue);
                        const isPlannedValid =
                          plannedDraftValue === ''
                          || (Number.isFinite(plannedParsed) && plannedParsed >= 0);
                        const normalizedPlanned =
                          isPlannedValid && plannedDraftValue !== ''
                            ? Math.round(plannedParsed)
                            : null;
                        const plannedMarginPercent =
                          normalizedPlanned && normalizedPlanned > 0
                            ? ((currentPrice / normalizedPlanned) - 1) * 100
                            : null;
                        const hasChanges =
                          isDraftValid
                          && isPlannedValid
                          && (
                            (normalizedDraft !== null && normalizedDraft !== currentPrice)
                            || normalizedPlanned !== normalizedCurrentPlanned
                          );

                        return (
                          <tr key={product.id} className="border-b border-border/40 last:border-0">
                            <td className="py-3 pr-4">
                              <p className="font-medium">{product.name}</p>
                              <p className="text-xs text-muted-foreground">ID: {product.id}</p>
                            </td>
                            <td className="py-3 pr-4">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={plannedDraftValue}
                                onChange={(event) =>
                                  handlePlannedPriceDraftChange(product.id, event.target.value)
                                }
                                className="input-zinga max-w-[140px]"
                                placeholder="—"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              {plannedMarginPercent !== null ? (
                                <span>
                                  {plannedMarginPercent.toLocaleString('hu-HU', {
                                    maximumFractionDigits: 2,
                                  })}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              {unitPurchasePrice !== null ? (
                                <div>
                                  <p>{formatFt(unitPurchasePrice)}/db</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatFt(netTotalValue)} / {stockQty} db
                                  </p>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Nincs adat</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              {marginPercent !== null ? (
                                <span>
                                  {marginPercent.toLocaleString('hu-HU', { maximumFractionDigits: 2 })}%
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={draftValue}
                                onChange={(event) =>
                                  handleProductPriceDraftChange(product.id, event.target.value)
                                }
                                className="input-zinga max-w-[140px]"
                              />
                            </td>
                            <td className="py-3 pr-0 text-right">
                              <button
                                type="button"
                                onClick={() => void handleSaveProductPrice(product)}
                                disabled={!hasChanges || savingProductId === product.id}
                                className="btn-secondary text-xs"
                              >
                                {savingProductId === product.id ? 'Mentés...' : 'Árak mentése'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            ) : null}
          </div>
        </div>
      </div>
    </Layout>
  );
}
